import { useContext, useState } from "react";
import { DataContext } from "../../context/data-context";
import { UtilityContext } from "../../context/util-context";
import SidebarButton from "../UI/SidebarButton";
import { useLocation } from "react-router-dom";
import { toTitleCase } from "../../util/format-text";
import { FaCarCrash, FaUserCircle } from "react-icons/fa";
import { GiTowTruck } from "react-icons/gi";
import {
  MdAddChart,
  MdBolt,
  MdBookmarks,
  MdChat,
  MdConstruction,
  MdDirectionsCar,
  MdDiscount,
  MdEditCalendar,
  MdHistory,
  MdLocationOn,
  MdOutgoingMail,
  MdOutlineExpand,
  MdPayments,
} from "react-icons/md";
import { BiSolidMegaphone } from "react-icons/bi";

const menus = [
  "general",
  "bookings",
  "payments",
  "expenses",
  "customers",
  "vehicles",
  "partners",
  "employees",
];

const icons = [
  { name: "applications", icon: MdDirectionsCar },
  { name: "audits", icon: MdHistory },
  { name: "charging", icon: MdBolt },
  { name: "claims", icon: FaCarCrash },
  { name: "coupons", icon: MdDiscount },
  { name: "announcements", icon: BiSolidMegaphone },
  { name: "locations", icon: MdLocationOn },
  { name: "maintenance", icon: MdConstruction },
  { name: "opportunities", icon: MdAddChart },
  { name: "billing", icon: MdPayments },
  { name: "quotes", icon: MdOutgoingMail },
  { name: "repossessions", icon: GiTowTruck },
  { name: "service", icon: MdEditCalendar },
  { name: "support", icon: MdChat },
  { name: "tolls", icon: MdBookmarks },
];

/** ========================================================================
 * * Sidebar Component
 * Renders the left navigation bar in large viewports.
 * ========================================================================== */

export default function Sidebar() {
  const location = useLocation();
  const { navigate } = useContext(UtilityContext);
  const { admin } = useContext(DataContext);

  // Initialize States
  const [iconTitle, setIconTitle] = useState("");
  const [expand, setExpand] = useState(false);
  const [menu, setMenu] = useState(menus);

  // Get page address from url
  const selected = location.pathname.slice(1).split("/")[0];

  /* ======================= EXPAND MENU HANDLER ======================= */
  const expandMenu = () => {
    if (expand) {
      setMenu(menus);
      setExpand(false);
    } else {
      setMenu([...icons.map((icon) => icon.name), ...menus]);
      setExpand(true);
    }
  };

  /* ======================= SELECTION HANDLER ======================= */
  const selectionHandler = (selected, e) => {
    navigate(`/${selected.toLowerCase()}`, e);
  };

  /* ======================= ICON CLASSES ======================= */
  const iconClasses = (icon) => {
    let classes = "text-2xl icon";
    if (icon === selected) classes += " text-yellow-600";
    return classes;
  };

  return (
    <div className="container relative hidden gap-6 md:grid">
      <div
        className="grid gap-2 justify-items-center clickable"
        onClick={() => navigate(`/employees/info?id=${admin.id}`)}
      >
        {admin?.photo ? (
          <img src={admin.photo} className="w-16 h-16 rounded-full" />
        ) : (
          <FaUserCircle className="w-16 h-16 text-yellow-600" />
        )}
        {admin.name ? (
          <p className="text-sm text-stone-200">{admin.name}</p>
        ) : (
          <p className="w-32 h-5 rounded-md animate-pulse bg-stone-700"></p>
        )}
      </div>

      <div className="relative grid gap-4 mb-4 rounded-lg md:grid-cols-4 justify-items-center">
        {icons.map(({ name, icon: IconComponent }) => (
          <IconComponent
            key={name}
            className={iconClasses(name)}
            onClick={(e) => selectionHandler(name, e)}
            onMouseEnter={() => setIconTitle(name)}
            onMouseLeave={() => setIconTitle("")}
          />
        ))}
        <MdOutlineExpand
          className={`icon ${expand ? "text-green-500" : ""}`}
          onClick={expandMenu}
          onMouseEnter={() =>
            setIconTitle(expand ? "Collapse Menu" : "Expand Menu")
          }
          onMouseLeave={() => setIconTitle("")}
        />

        <p className="absolute text-sm text-center rounded-lg -bottom-8 text-stone-400">
          {toTitleCase(iconTitle)}
        </p>
      </div>

      <hr className="hr" />
      <div className="grid gap-6">
        {menu.map((item, index) => (
          <SidebarButton
            key={index}
            title={item}
            selected={selected.length === 0 ? "general" : selected}
            onClick={(e) => selectionHandler(item, e)}
          />
        ))}
      </div>
      <hr className="hr" />
    </div>
  );
}
